import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import PlusCircleIcon from '../../../components/icons/PlusCircleIcon';
import { canManageOwnContent, TEACHER_TRAINING_MANAGE_BASE } from '../../../config/contentRoutes';

interface TrainingPage {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'upcoming' | 'active';
  duration: string;
  date: string;
  instructor: string;
  path: string;
  category: string;
  tags: string[];
  viewCount?: number;
}

interface TrainingRecord {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD 형식
  status: 'completed' | 'upcoming' | 'active';
  category: string;
  path: string;
}

/** 영역별 연수 횟수(레이더) 집계 시 3D 프린팅·모델링을 동일 영역으로 취급 */
const RADAR_MERGED_3D_LABEL = '3D프린팅·모델링';

const categoryKeyForRadarStats = (category: string): string =>
  category === '3D모델링' || category === '3D프린팅' ? RADAR_MERGED_3D_LABEL : category;

const TrainingMainPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const canManage = canManageOwnContent(currentUser?.role);
  const [isMobile, setIsMobile] = useState(false);
  const [currentNoticeContent, setCurrentNoticeContent] = useState('교사 연수 프로그램을 확인하세요');
  const [noticeStage, setNoticeStage] = useState<'enter' | 'exit'>('enter');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // 연수 페이지 데이터 (조회수 포함)
  const trainingPages: TrainingPage[] = [
    {
      id: 'makecode-arcade',
      title: 'MakeCode Arcade 연수',
      description: '게임 개발을 통한 프로그래밍 교육',
      status: 'completed',
      duration: '4시간',
      date: '2024-01-15',
      instructor: '김교사',
      path: '/teacher-training/training/completed/MakeCodeArcadeTraining',
      category: '프로그래밍',
      tags: ['MakeCode', '게임개발', '기초'],
      viewCount: 156
    },
    {
      id: '3d-modeling',
      title: '3D 모델링 연수',
      description: '3D 프린팅과 모델링 기초',
      status: 'upcoming',
      duration: '3시간',
      date: '2024-02-10',
      instructor: '박교사',
      path: '/teacher-training/training/upcoming/ThreeDModelingTraining',
      category: '3D모델링',
      tags: ['3D프린팅', '모델링', '디자인'],
      viewCount: 98
    },
    {
      id: 'deagwang-ai-edutech',
      title: '대광고등학교 AI 에듀테크 연수',
      description: '교육현장에서 AI 에듀테크를 활용한 수업 설계 및 실습',
      status: 'active',
      duration: '2시간',
      date: '2025-10-01',
      instructor: '한지호',
      path: '/teacher-training/digital/DeaGwamgAIEdutechTraining',
      category: 'AI',
      tags: ['AI', '에듀테크', '수업설계', '교육혁신'],
      viewCount: 203
    },
    {
      id: 'elementary-3d-printing-2026',
      title: '2026 「(초등) 3D프린팅 활용 직무연수」',
      description: '3D프린팅·Sculpt 모델링·굿즈 제작 (서울융합과학원)',
      status: 'upcoming',
      duration: '3시간',
      date: '2026-04-09',
      instructor: '담당 강사',
      path: '/teacher-training/digital/Elementary3DPrintingTraining2026',
      category: '3D프린팅',
      tags: ['3D프린팅', 'Sculpt', '직무연수', '초등'],
      viewCount: 0
    },
    {
      id: 'middle-school-3d-printing-2026',
      title: '2026 「(중등) 3D프린팅 활용 직무연수」',
      description: '3D프린팅·Sculpt 모델링·굿즈 제작 (서울융합과학원)',
      status: 'upcoming',
      duration: '3시간',
      date: '2026-04-09',
      instructor: '담당 강사',
      path: '/teacher-training/digital/MiddleSchool3DPrintingTraining2026',
      category: '3D프린팅',
      tags: ['3D프린팅', 'Sculpt', '직무연수', '중등'],
      viewCount: 0
    },
    {
      id: 'central-middle-debeth-ai-digital-leader-2026',
      title: '중부 AI에듀테크 담당자 연수',
      description: '2026 중부 중등 디벗 핵심교원 AI·디지털 리더 연수 — 아이패드·MDM·수업 사례 (상연재 서울시청점)',
      status: 'upcoming',
      duration: '3시간',
      date: '2026-04-22',
      instructor: '담당 강사',
      path: '/teacher-training/digital/CentralMiddleDebethAIDigitalLeaderTraining2026',
      category: 'AI·디벗',
      tags: ['중부', '디벗', '아이패드', 'MDM', 'AI'],
      viewCount: 0
    },
    {
      id: 'namdaemoon-middle-debeth-2026',
      title: '남대문중학교 디벗 연수',
      description: 'AI·디지털 동향, 학습지원 SW 심의, SEN 스쿨·Canva·Seam 스페이스·전자칠판 활용',
      status: 'active',
      duration: '2시간',
      date: '2026-06-11',
      instructor: '한지호',
      path: '/teacher-training/digital/NamdaemoonMiddleSchoolDebethTraining2026',
      category: 'AI·디벗',
      tags: ['남대문중', '디벗', 'AI', 'SEN스쿨', 'Canva', 'Seam스페이스'],
      viewCount: 0
    },
    {
      id: 'namdaemoon-middle-debeth-2026-session2',
      title: '남대문중학교 디벗 연수 2회차',
      description: 'ZEP School·ZEP Quiz·QuizN·Google Vids·Gemini·NotebookLM 참여형 수업',
      status: 'active',
      duration: '2시간',
      date: '2026-06-19',
      instructor: '한지호',
      path: '/teacher-training/digital/NamdaemoonMiddleSchoolDebethTraining2026Session2',
      category: 'AI·디벗',
      tags: ['남대문중', '디벗', 'ZEP', 'QuizN', 'Google Vids', 'NotebookLM'],
      viewCount: 0
    },
    {
      id: 'ewha-geumran-middle-ai-2026',
      title: '이화금란중학교 AI 업무 간소화 연수',
      description: 'Gemini·GPT 기반 가정통신문·안내문 작성, 이수증 정리, 업무 자동화 설계',
      status: 'upcoming',
      duration: '100분',
      date: '2026-06-15',
      instructor: '한지호',
      path: '/teacher-training/digital/EwhaGeumranMiddleSchoolAITraining2026',
      category: 'AI',
      tags: ['이화금란중', 'Gemini', 'GPT', '가정통신문', '업무경감'],
      viewCount: 0
    },
    {
      id: 'jingwan-middle-ai-2026',
      title: '진관중학교 AI 기반 수업 준비 및 자료 작성 연수',
      description: 'ChatGPT·Gemini·Claude·Copilot·Perplexity 비교, HWPX 실습, PPT 제작 흐름, 바이브코딩',
      status: 'upcoming',
      duration: '100분',
      date: '2026-06-24',
      instructor: '한지호',
      path: '/teacher-training/digital/JingwanMiddleSchoolAITraining2026',
      category: 'AI',
      tags: ['진관중', 'ChatGPT', 'Gemini', 'Claude', 'Copilot', '수업자료'],
      viewCount: 0
    }
  ];

  // 조회수 기준으로 정렬 (높은 순)
  const popularPages = [...trainingPages]
    .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
    .slice(0, 4); // 상위 4개만 표시

  // 연수 기록 데이터 (실제로는 Firebase에서 가져올 수 있음)
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([
    {
      id: 'makecode-arcade',
      title: 'MakeCode Arcade 연수',
      date: '2024-01-15',
      status: 'completed',
      category: '프로그래밍',
      path: '/teacher-training/training/completed/MakeCodeArcadeTraining'
    },
    {
      id: 'deagwang-ai-edutech',
      title: '대광고등학교 AI 에듀테크 연수',
      date: '2025-10-01',
      status: 'active',
      category: 'AI',
      path: '/teacher-training/digital/DeaGwamgAIEdutechTraining'
    },
    {
      id: '3d-modeling',
      title: '3D 모델링 연수',
      date: '2024-02-10',
      status: 'upcoming',
      category: '3D모델링',
      path: '/teacher-training/training/upcoming/ThreeDModelingTraining'
    },
    {
      id: 'elementary-3d-printing-2026',
      title: '2026 「(초등) 3D프린팅 활용 직무연수」',
      date: '2026-04-09',
      status: 'upcoming',
      category: '3D프린팅',
      path: '/teacher-training/digital/Elementary3DPrintingTraining2026'
    },
    {
      id: 'middle-school-3d-printing-2026',
      title: '2026 「(중등) 3D프린팅 활용 직무연수」',
      date: '2026-04-09',
      status: 'upcoming',
      category: '3D프린팅',
      path: '/teacher-training/digital/MiddleSchool3DPrintingTraining2026'
    },
    {
      id: 'central-middle-debeth-ai-digital-leader-2026',
      title: '중부 AI에듀테크 담당자 연수',
      date: '2026-04-22',
      status: 'upcoming',
      category: 'AI·디벗',
      path: '/teacher-training/digital/CentralMiddleDebethAIDigitalLeaderTraining2026'
    },
    {
      id: 'namdaemoon-middle-debeth-2026',
      title: '남대문중학교 디벗 연수',
      date: '2026-06-11',
      status: 'active',
      category: 'AI·디벗',
      path: '/teacher-training/digital/NamdaemoonMiddleSchoolDebethTraining2026'
    },
    {
      id: 'ewha-geumran-middle-ai-2026',
      title: '이화금란중학교 AI 업무 간소화 연수',
      date: '2026-06-15',
      status: 'upcoming',
      category: 'AI',
      path: '/teacher-training/digital/EwhaGeumranMiddleSchoolAITraining2026'
    },
    {
      id: 'jingwan-middle-ai-2026',
      title: '진관중학교 AI 기반 수업 준비 및 자료 작성 연수',
      date: '2026-06-24',
      status: 'upcoming',
      category: 'AI',
      path: '/teacher-training/digital/JingwanMiddleSchoolAITraining2026'
    }
  ]);

  // 오늘 날짜와 비교해서 지난 연수는 자동으로 완료 상태로 변경
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 시간을 00:00:00으로 설정하여 날짜만 비교

    setTrainingRecords(prevRecords => 
      prevRecords.map(record => {
        const recordDate = new Date(record.date);
        recordDate.setHours(0, 0, 0, 0);
        
        // 오늘보다 이전이고 아직 완료되지 않은 경우 완료로 변경
        if (recordDate < today && record.status !== 'completed') {
          return { ...record, status: 'completed' as const };
        }
        return record;
      })
    );
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 캘린더 생성 함수
  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    const calendar = [];
    
    // 이전 달의 날짜들
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      calendar.push({
        date: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        hasTraining: false
      });
    }
    
    // 현재 달의 날짜들
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasTraining = trainingRecords.some(record => record.date === dateStr);
      const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
      const isSelected = selectedDate && 
        day === selectedDate.getDate() && 
        month === selectedDate.getMonth() && 
        year === selectedDate.getFullYear();
      
      calendar.push({
        date: day,
        isCurrentMonth: true,
        isToday,
        isSelected,
        hasTraining,
        dateStr
      });
    }
    
    // 다음 달의 날짜들
    const remainingCells = 42 - calendar.length;
    for (let day = 1; day <= remainingCells; day++) {
      calendar.push({
        date: day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        hasTraining: false
      });
    }
    
    return calendar;
  };

  // 월 변경 함수들
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // 날짜 클릭 함수
  const handleDateClick = (day: number, isCurrentMonth: boolean, dateStr?: string) => {
    if (isCurrentMonth && dateStr) {
      const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      setSelectedDate(clickedDate);
    }
  };

  // 선택된 날짜의 연수 기록 가져오기
  const getSelectedDateTrainings = () => {
    if (!selectedDate) return [];
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
    return trainingRecords.filter(record => record.date === dateStr);
  };

  const selectedDateTrainings = getSelectedDateTrainings();
  const calendar = generateCalendar();
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleOpenAddNoticeDialog = () => {
    // TODO: 알림 등록 다이얼로그 열기
    console.log('알림 등록');
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      minHeight: 0,
      padding: isMobile ? '16px' : '0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* 연수 현황 정보 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gap-level-3, 8px)',
        alignSelf: 'stretch',
        paddingLeft: isMobile ? '0' : '0',
        paddingRight: isMobile ? '0' : '0',
        flexShrink: 0,
      }}>
        <div 
          style={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}
        >
          🎓
        </div>
        <div style={{ 
          margin: 0,
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: 'var(--Font-Font-size-Body-2, 16px)',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px'
        }}>
          교사 연수 프로그램
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto'
        }}>
          {canManage && (
            <button
              type="button"
              onClick={() => navigate(TEACHER_TRAINING_MANAGE_BASE)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal)',
                background: 'var(--Color-background-normal)',
                color: 'var(--Color-text-normal)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              내 연수 관리
            </button>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '8px',
            background: 'var(--Color-background-sub, #F8F8F8)'
          }}>
            <span style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'Pretendard',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px'
            }}>
              완료
            </span>
            <span style={{
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '18px'
            }}>
              0
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '8px',
            background: 'var(--Color-background-sub, #F8F8F8)'
          }}>
            <span style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontFamily: 'Pretendard',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px'
            }}>
              진행중
            </span>
            <span style={{
              color: 'var(--Color-text-normal, #161616)',
              fontFamily: 'Pretendard',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '18px'
            }}>
              0
            </span>
          </div>
        </div>
      </div>
      {/* 인사말 헤더 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gap-level-3, 8px)',
        alignSelf: 'stretch',
        padding: isMobile ? '8px 0px 16px 0px' : '8px 0px 32px 0px',
        flexShrink: 0,
      }}>
        <h1 style={{ 
          color: 'var(--Color-text-normal, #161616)',
          fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
          fontSize: isMobile ? '24px' : 'var(--Font-Font-size-Heading-2, 32px)',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: isMobile ? '-0.48px' : '-0.64px',
          margin: 0
        }}>
          교사 연수에 오신 것을 환영합니다
        </h1>
      </div>
      {/* 중요 알림 창 */}
      <div style={{ 
        width: '100%',
        height: isMobile ? 'auto' : '40px',
        minHeight: isMobile ? 'auto' : '40px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: isMobile ? '12px' : '16px',
        marginBottom: isMobile ? '12px' : '18px',
        paddingLeft: isMobile ? '0' : '0',
        paddingRight: isMobile ? '0' : '0',
        flexShrink: 0,
      }}>
        <div style={{ 
          display: 'flex',
          width: isMobile ? '100%' : '1172px',
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? '40px' : '100%',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '8px',
          border: '1.2px solid #293044',
          background: '#293044',
          padding: isMobile ? '12px' : '0',
        }}>
          <div style={{
            display: 'flex',
            marginLeft: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            width: '40px',
            height: '22px',
            flexShrink: 0,
            borderRadius: '8px',
            border: '1px solid #F0F0F0',
            background: '#FFF'
          }}>
            <span style={{
              color: 'var(--Color-status-negative, #FF3838)',
              fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
              fontSize: 'var(--Font-Font-size-Label-2, 12px)',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '150%'
            }}>
              중요
            </span>
          </div>
          <p style={{
            color: 'var(--Color-text-inverse, #FFF)',
            fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
            fontSize: 'var(--Font-Font-size-Body-4, 13px)',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '150%',
            margin: 0,
            flex: 1,
            transform: noticeStage === 'enter' ? 'translateY(0)' : 'translateY(-0.5rem)',
            opacity: noticeStage === 'enter' ? 1 : 0,
            transition: 'opacity 0.3s ease, transform 0.3s ease'
          }}>
            {currentNoticeContent}
          </p>
        </div>
        <Button
          width={isMobile ? "100%" : "112px"}
          height={isMobile ? "40px" : "100%"}
          size="medium"
          variant="submit"
          onClick={handleOpenAddNoticeDialog}
          leftIcon={<PlusCircleIcon width={14} height={13} color="white" />}
        >
          알림 등록
        </Button>
      </div>
      
      {/* 대시보드 전체 영역 - 3등분 */}
      <div
        style={{
          width: isMobile ? '100%' : '1280px',
          height: isMobile ? '100%' : '702px',
          minHeight: isMobile ? 0 : '702px',
          gap: isMobile ? '12px' : '24px',
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
          justifyContent: 'flex-start', 
          flex: isMobile ? 1 : undefined,
          overflow: isMobile ? 'hidden' : undefined,
        }}
      >
        {/* 첫 번째 영역 - 내가 한 연수 */}
        <div style={{
          width: isMobile ? '100%' : 'calc((100% - 48px) / 3)',
          height: isMobile ? 'auto' : '702px',
          minHeight: isMobile ? 0 : '702px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '12px' : '24px',
          flex: isMobile ? 1 : undefined,
          overflow: isMobile ? 'hidden' : undefined,
        }}>
          {/* 위쪽 카드 - 레이더 그래프 */}
          <Card
            width="100%"
            height={isMobile ? "auto" : "360px"}
            padding="16px 20px"
            alignItems="flex-start"
            gap={10}
            style={{ 
              display: 'inline-flex', 
              overflow: 'hidden',
              flexShrink: 0,
              minHeight: isMobile ? 0 : "360px"
            }} 
          >
            <div style={{
              width: '100%',
              height: '100%',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '20px',
              display: 'flex',
            }}>
              {/* 제목 */}
              <div style={{
                width: '100%',
                height: '27px',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                color: 'var(--Color-text-normal, #161616)',
                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '150%',
                letterSpacing: '-0.18px',
                margin: 0
              }}>
                영역별 연수 횟수
              </div>

              {/* 영역별 연수 횟수 레이더 그래프 */}
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--gap-level-4)',
                flex: 1,
                minHeight: 0,
                paddingTop: '8px',
              }}>
                {(() => {
                  // 카테고리별 연수 횟수 계산 (3D 프린팅·모델링은 한 축으로 합산)
                  const categoryCounts: { [key: string]: number } = {};
                  trainingRecords.forEach(record => {
                    const key = categoryKeyForRadarStats(record.category);
                    categoryCounts[key] = (categoryCounts[key] || 0) + 1;
                  });
                  
                  const categories = Object.keys(categoryCounts);
                  const counts = Object.values(categoryCounts);
                  const maxCount = Math.max(...counts, 1); // 최대값 (0으로 나누기 방지)
                  
                  if (categories.length === 0) {
                    return (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontFamily: 'Pretendard',
                        fontSize: '12px'
                      }}>
                        연수 기록이 없습니다.
                      </div>
                    );
                  }

                  const centerX = 180;
                  const centerY = 150;
                  const radius = 62;
                  const labelInsetFromLabelRing = 18;
                  const angleStep = (2 * Math.PI) / categories.length;

                  // 레이더 그래프 그리기
                  const points = categories.map((_, index) => {
                    const angle = (index * angleStep) - (Math.PI / 2); // 위쪽부터 시작
                    const value = counts[index] / maxCount;
                    const x = centerX + radius * value * Math.cos(angle);
                    const y = centerY + radius * value * Math.sin(angle);
                    return { x, y, angle, label: categories[index], value: counts[index] };
                  });

                  return (
                    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 8px 20px' }}>
                      <svg width="360" height="300" viewBox="0 0 360 300" style={{ overflow: 'visible', maxWidth: '100%' }}>
                        {/* 배경 그리드 */}
                        {[0.25, 0.5, 0.75, 1].map((scale, i) => (
                          <polygon
                            key={`grid-${i}`}
                            points={categories.map((_, index) => {
                              const angle = (index * angleStep) - (Math.PI / 2);
                              const x = centerX + radius * scale * Math.cos(angle);
                              const y = centerY + radius * scale * Math.sin(angle);
                              return `${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke="var(--Color-line-normal, #F0F0F0)"
                            strokeWidth="1"
                          />
                        ))}
                        
                        {/* 축선 */}
                        {categories.map((_, index) => {
                          const angle = (index * angleStep) - (Math.PI / 2);
                          const x = centerX + radius * Math.cos(angle);
                          const y = centerY + radius * Math.sin(angle);
                          return (
                            <line
                              key={`axis-${index}`}
                              x1={centerX}
                              y1={centerY}
                              x2={x}
                              y2={y}
                              stroke="var(--Color-line-normal, #F0F0F0)"
                              strokeWidth="1"
                            />
                          );
                        })}

                        {/* 데이터 영역 */}
                        <polygon
                          points={points.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="var(--Color-fill-brand-color, #FF6F0F)"
                          fillOpacity="0.3"
                          stroke="var(--Color-fill-brand-color, #FF6F0F)"
                          strokeWidth="2"
                        />

                        {/* 데이터 포인트 */}
                        {points.map((point, index) => (
                          <circle
                            key={`point-${index}`}
                            cx={point.x}
                            cy={point.y}
                            r="4"
                            fill="var(--Color-fill-brand-color, #FF6F0F)"
                          />
                        ))}

                        {/* 라벨 */}
                        {points.map((point, index) => {
                          const labelRadius = radius + 52;
                          const labelX = centerX + labelRadius * Math.cos(point.angle);
                          const labelY = centerY + labelRadius * Math.sin(point.angle);
                          return (
                            <g key={`label-${index}`}>
                              <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="11"
                                fontFamily="Pretendard"
                                fill="var(--Color-text-normal, #161616)"
                                fontWeight="500"
                                style={{ wordBreak: 'break-word' }}
                              >
                                {point.label}
                              </text>
                              <text
                                x={labelX}
                                y={labelY + labelInsetFromLabelRing}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="10"
                                fontFamily="Pretendard"
                                fill="var(--Color-text-assistive, #989898)"
                              >
                                {point.value}회
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>

          {/* 아래쪽 카드 - 연수 목록 */}
          <Card
            width="100%"
            height={isMobile ? "auto" : "318px"}
            padding="16px 20px"
            alignItems="flex-start"
            gap={10}
            style={{ 
              display: 'inline-flex', 
              overflow: 'hidden',
              flex: isMobile ? 1 : undefined,
              minHeight: isMobile ? 0 : "318px"
            }} 
          >
            <div style={{
              width: '100%',
              height: '100%',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: 16,
              display: 'flex',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}>
              {/* 제목 */}
              <div style={{
                width: '100%',
                height: '27px',
                display: 'flex',
                flexDirection: 'column',
                color: 'var(--Color-text-normal, #161616)',
                fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '150%',
                letterSpacing: '-0.18px',
                margin: 0,
                flexShrink: 0
              }}>
                내가 한 연수
              </div>

              {/* 연수 목록 */}
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                minHeight: 0
              }}>
                {trainingRecords
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // 최신순 정렬
                  .map((training) => (
                    <Card
                      key={training.id}
                      width="100%"
                      height="auto"
                      padding="12px 16px"
                      alignItems="flex-start"
                      gap={8}
                      onClick={() => navigate(training.path)}
                      style={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
                      }}
                    >
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                      }}>
                        {/* 왼쪽: 제목 및 카테고리 */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          flex: 1,
                          minWidth: 0
                        }}>
                          <h4 style={{
                            color: 'var(--Color-text-strong, #0C0C0D)',
                            fontFamily: 'Pretendard',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '21px',
                            letterSpacing: '-0.01em',
                            margin: 0,
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {training.title}
                          </h4>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '400',
                              lineHeight: '16.5px',
                              color: 'var(--Color-text-assistive, #989898)',
                              fontFamily: 'Pretendard',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              background: 'var(--Color-background-sub, #F8F8F8)',
                              border: '1px solid var(--Color-line-normal, #F0F0F0)'
                            }}>
                              {training.category}
                            </span>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '400',
                              lineHeight: '16.5px',
                              color: 'var(--Color-text-assistive, #989898)',
                              fontFamily: 'Pretendard'
                            }}>
                              {training.status === 'completed' ? '완료' : training.status === 'active' ? '진행중' : '예정'}
                            </span>
                          </div>
                        </div>

                        {/* 오른쪽: 날짜 및 상태 표시 */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '4px',
                          flexShrink: 0
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '18px',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontFamily: 'Pretendard',
                            whiteSpace: 'nowrap'
                          }}>
                            {training.date}
                          </span>
                          <div style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: training.status === 'completed' 
                              ? 'var(--Color-status-positive, #00C853)' 
                              : training.status === 'active'
                              ? 'var(--Color-fill-brand-color, #FF6F0F)'
                              : 'var(--Color-status-warning, #FFA726)',
                            alignSelf: 'flex-end'
                          }} />
                        </div>
                      </div>
                    </Card>
                  ))}
              </div>
            </div>
          </Card>
        </div>

        {/* 두 번째 카드 - 캘린더 및 연수 기록 */}
        <div style={{
          width: isMobile ? '100%' : 'calc((100% - 48px) / 3)',
          height: isMobile ? '100%' : '702px',
          minHeight: isMobile ? 0 : '702px',
          gap: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: isMobile ? 1 : undefined,
          overflow: isMobile ? 'hidden' : undefined,
        }}>
          {/* 캘린더 카드 */}
          <Card
            height={isMobile ? "auto" : "337px"}
            padding="16px 20px 20px 20px"
            alignItems="flex-start"
            borderBottomRightRadius="0"
            borderBottomLeftRadius="0"
            gap={0}
            style={{
              display: 'inline-flex',
              minHeight: isMobile ? 0 : "337px"
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '16px'
            }}>
              {/* 캘린더 헤더 */}
              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  lineHeight: '27px',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'Pretendard',
                  margin: 0
                }}>
                  {currentDate.getFullYear()}년 {monthNames[currentDate.getMonth()]}
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}>
                  <div 
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px'
                    }}
                    onClick={goToPreviousMonth}
                  >
                    <span style={{ fontSize: '20px', color: 'var(--Color-text-normal, #161616)' }}>‹</span>
                  </div>
                  <div 
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px'
                    }}
                    onClick={goToNextMonth}
                  >
                    <span style={{ fontSize: '20px', color: 'var(--Color-text-normal, #161616)' }}>›</span>
                  </div>
                </div>
              </div>

              {/* 캘린더 그리드 */}
              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '12px'
              }}>
                {/* 요일 헤더 */}
                <div style={{ 
                  width: '100%',
                  height: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '24px',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'Pretendard',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '150%',
                }}>
                  {weekDays.map(day => (
                    <span key={day} style={{ textAlign: 'center' }}>{day}</span>
                  ))}
                </div>
                
                {/* 날짜 그리드 */}
                <div style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  columnGap: '24px',
                  rowGap: '4px'
                }}>
                  {calendar.map((day, index) => (
                    <div 
                      key={index}
                      onClick={() => handleDateClick(day.date, day.isCurrentMonth, day.dateStr)}
                      style={{ 
                        display: 'flex',
                        width: '100%',
                        height: '34px',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: day.isCurrentMonth ? 'pointer' : 'default'
                      }}
                    >
                      {/* 날짜 */}
                      <div style={{
                        display: 'flex', 
                        width: '24px',
                        height: '24px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: '0',
                        aspectRatio: '1/1',   
                        color: day.isCurrentMonth 
                          ? (day.isSelected ? 'var(--Color-text-inverse, #FFF)' : 'var(--Color-text-normal, #161616)')
                          : 'var(--Color-text-assistive, #989898)',
                        backgroundColor: day.isSelected ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                        borderRadius: day.isSelected ? '50%' : '0',
                        textAlign: 'center',
                        fontFamily: 'Pretendard',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '150%'
                      }}>
                        {day.date}
                      </div>
                      {/* 연수 표시 아이콘 */}
                      <div style={{
                        display: 'flex',
                        width: '100%',
                        height: '4px',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {day.isCurrentMonth && day.hasTraining && (
                          <div style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)'
                          }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* 연수 기록 카드 */}
          <Card
            width="100%"
            height={isMobile ? "auto" : "305px"}
            padding="16px 20px"
            alignItems="flex-start"
            borderTopRightRadius="0"
            borderTopLeftRadius="0"
            borderTop="1px solid var(--Color-line-normal, #F0F0F0)"
            gap={0}
            style={{
              display: 'inline-flex',
              minHeight: isMobile ? 0 : "305px",
              flex: isMobile ? 1 : undefined,
              overflow: isMobile ? 'hidden' : 'auto'
            }}
          >
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '4px'
            }}>
              {/* 헤더 */}
              <div style={{
                width: '100%',
                height: '27px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px',
                padding: '8px 0px 8px 0px'
              }}>
                <span style={{ fontSize: '20px' }}>📅</span>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  lineHeight: '27px',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'Pretendard',
                  margin: 0
                }}>
                  {selectedDate 
                    ? `${selectedDate.getDate()}일 ${weekDays[selectedDate.getDay()]}요일 연수 기록`
                    : '연수 기록을 확인하려면 날짜를 선택하세요'
                  }
                </h3>
              </div>

              {/* 연수 기록 목록 */}
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '8px',
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                {selectedDate ? (
                  selectedDateTrainings.length > 0 ? (
                    selectedDateTrainings.map((training) => (
                      <div 
                        key={training.id}
                        onClick={() => navigate(training.path)}
                        style={{
                          width: '100%',
                          minHeight: '82px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          padding: '16px 0px',
                          gap: '8px',
                          borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--Color-background-sub, #F8F8F8)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: '11px'
                        }}>
                          <div style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: training.status === 'completed' 
                              ? 'var(--Color-status-positive, #00C853)' 
                              : training.status === 'active'
                              ? 'var(--Color-fill-brand-color, #FF6F0F)'
                              : 'var(--Color-status-warning, #FFA726)'
                          }} />
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '21px',
                            color: 'var(--Color-text-normal, #161616)',
                            fontFamily: 'Pretendard',
                            flex: 1
                          }}>
                            {training.title}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '18px',
                            color: 'var(--Color-text-assistive, #989898)',
                            fontFamily: 'Pretendard',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            background: 'var(--Color-background-sub, #F8F8F8)',
                            border: '1px solid var(--Color-line-normal, #F0F0F0)'
                          }}>
                            {training.category}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '400',
                          lineHeight: '18px',
                          color: 'var(--Color-text-assistive, #989898)',
                          fontFamily: 'Pretendard',
                          marginLeft: '18px'
                        }}>
                          {training.status === 'completed' ? '완료' : training.status === 'active' ? '진행중' : '예정'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      width: '100%',
                      padding: '40px 0',
                      textAlign: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'Pretendard',
                      fontSize: '14px'
                    }}>
                      선택한 날짜에 연수 기록이 없습니다.
                    </div>
                  )
                ) : (
                  <div style={{
                    width: '100%',
                    padding: '40px 0',
                    textAlign: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontFamily: 'Pretendard',
                    fontSize: '14px'
                  }}>
                    날짜를 선택하면 해당 날짜의 연수 기록을 확인할 수 있습니다.
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* 세 번째 카드 - 인기 연수 페이지 */}
        <div style={{
          width: isMobile ? '100%' : 'calc((100% - 48px) / 3)',
          height: isMobile ? 'auto' : '702px',
          minHeight: isMobile ? 0 : '702px',
          display: 'flex',
          flexDirection: 'column',
          flex: isMobile ? 1 : undefined,
          overflow: isMobile ? 'hidden' : undefined,
        }}>
            {/* 인기 연수 페이지 카드 */}
            <Card
              width="100%"
              height="100%"
              padding="16px 20px"
              alignItems="flex-start"
              gap={10}
              style={{ 
                display: 'inline-flex', 
                minHeight: "100%",
                overflow: 'hidden'
              }} 
            >
              <div style={{
                width: '100%',
                height: '100%',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 24,
                display: 'flex',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}>
                {/* 제목 */}
                <div style={{
                  width: '100%',
                  height: '27px',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'var(--Color-text-normal, #161616)',
                  fontFamily: 'var(--Font-Typeface-Type, Pretendard)',
                  fontSize: 'var(--Font-Font-size-Heading-6, 18px)',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '150%',
                  letterSpacing: '-0.18px',
                  margin: 0
                }}>
                  인기 연수 페이지
                </div>

                {/* 연수 페이지 목록 */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  flex: 1,
                  minHeight: 0
                }}>
                  {popularPages.map((page, index) => (
                    <Card
                      key={page.id}
                      width="100%"
                      height="auto"
                      padding="16px"
                      alignItems="flex-start"
                      gap={8}
                      onClick={() => navigate(page.path)}
                      style={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
                      }}
                    >
                      <div style={{
                        width: '100%',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: 8,
                        display: 'flex'
                      }}>
                        {/* 순위 및 제목 */}
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {index < 3 && (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '8px',
                              background: index === 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 
                                         index === 1 ? '#FF9F5E' : 
                                         '#FFD2B5',
                              flexShrink: 0
                            }}>
                              <span style={{
                                color: 'var(--Color-text-inverse, #FFF)',
                                fontFamily: 'Pretendard',
                                fontSize: '12px',
                                fontWeight: '600',
                                lineHeight: '18px'
                              }}>
                                {index + 1}
                              </span>
                            </div>
                          )}
                          {index >= 3 && (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--Color-text-assistive, #989898)',
                              fontFamily: 'Pretendard',
                              fontSize: '12px',
                              fontWeight: '400',
                              lineHeight: '18px',
                              flexShrink: 0
                            }}>
                              {index + 1}
                            </div>
                          )}
                          <h4 style={{
                            flex: 1,
                            color: 'var(--Color-text-strong, #0C0C0D)',
                            fontFamily: 'Pretendard',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '21px',
                            letterSpacing: '-0.01em',
                            margin: 0,
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {page.title}
                          </h4>
                        </div>

                        {/* 설명 */}
                        <p style={{
                          width: '100%',
                          color: 'var(--Color-text-assistive, #989898)',
                          fontFamily: 'Pretendard',
                          fontSize: '12px',
                          fontWeight: '400',
                          lineHeight: '18px',
                          letterSpacing: '0em',
                          margin: 0,
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {page.description}
                        </p>

                        {/* 조회수 및 카테고리 */}
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontFamily: 'Pretendard',
                              fontSize: '11px',
                              fontWeight: '400',
                              lineHeight: '16.5px'
                            }}>
                              👁
                            </span>
                            <span style={{
                              color: 'var(--Color-text-assistive, #989898)',
                              fontFamily: 'Pretendard',
                              fontSize: '11px',
                              fontWeight: '400',
                              lineHeight: '16.5px'
                            }}>
                              {page.viewCount || 0}
                            </span>
                          </div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '400',
                            lineHeight: '16.5px',
                            color: 'var(--Color-text-assistive, #989898)',
                            background: 'var(--Color-background-sub, #F8F8F8)',
                            border: '1px solid var(--Color-line-normal, #F0F0F0)'
                          }}>
                            {page.category}
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </Card>
          </div>
      </div>
    </div>
  );
};

export default TrainingMainPage;
